import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {_openModal} from '../../../shared/reducers/modals';
import {Button, Column, LoadingSpinner, Row, TextInput} from '../../../shared';
import debug from 'debug';
import {formatter} from '../../../shared/utils/utils';
import {isEntitled} from '../../../shared/reducers/user';
import useSWR from 'swr';
import axiosInstance from '../../../shared/utils/axios';
import useQueryObject from '../../../shared/utils/useQueryObject';
import history from '../../../history';

const log = debug('circleblack:advisor:account:summary');

/* // TODO Put into Model tile
{account.typeName.toUpperCase() !== 'BANK'
      && isEntitled([{group: 'models', feature: 'core', access: 'r'}])
      && <Button importance={'secondary'}
                 width={'auto'}
                 size={'md'}
                 label={'Rebalance'}
                 disabled={!isEntitled([
                   {
                     group: 'models',
                     feature: 'proposals',
                     access: 'c',
                   },
                 ])}
                 action={() => startManageAccountModels(account.id)}/>}
      {!!account.modelAssignedBy
      && isEntitled([{group: 'models', feature: 'core', access: 'r'}])
      && <Button importance={'secondary'}
                 width={'auto'}
                 size={'md'}
                 label={'Unassign Models'}
                 action={() => startUnassignModels(account.id, () => setAccount(c => ({...c, modelAssignedBy: null})))}/>}
      {isEntitled([{group: 'models', feature: 'core', access: 'r'}])
      && <Button importance={'secondary'}
                 width={'auto'}
                 size={'md'}
                 label={'Rebalancing History'}
                 disabled={!account.lastRebalance}
                 action={() => history.push(`/models/rebalancing/${account.id}/history`)}/>}
 */

const AccountInfoSummary = ({
  accountID,
  advisorID,
  session,
  openModal,
  updateContext,
}) => {
  const {data: account} = useSWR(`/accounts/${accountID}?skipAllocation=1&tpp=1`);
  const [context] = useQueryObject('context');
  const {data: household} = useSWR(
    account
    && account.householdID
    && context
    && `/containers/${context.view && context.view.containerID || context.containerID}/clients?limit=1&id=${account.householdID}`,
    url => axiosInstance.get(url).then(res => res.data[0])
  );

  log('account', account);

  if (!account) {
    return <LoadingSpinner/>;
  }

  return <Column height={'200px'} layout={{grow: 1}}>
    <Row wrap={true} justifySubRow={'left'}>
      <TextInput readOnly={true}
                 type={'text'}
                 width={'100px'}
                 layout={{grow: 1}}
                 title={'As of Date'}
                 dataPoint={{name: 'lastFeedAsOfDate', value: formatter('date')(account.lastFeedAsOfDate)}}
                 placeholder={''}/>
      <TextInput readOnly={true}
                 width={'100px'}
                 layout={{grow: 1}}
                 title={'Account #'}
                 dataPoint={{
                   name: 'accountNumber',
                   value: account.number,
                 }}
                 type={'accountNumber'}
                 institution={account.institution}
                 institutionName={account.institutionName}
                 placeholder={''}/>
      <TextInput readOnly={true}
                 type={'text'}
                 width={'100px'}
                 layout={{grow: 1}}
                 title={'Account Type'}
                 dataPoint={{name: 'typeName', value: account.typeName}}
                 placeholder={''}/>
      {isEntitled([{group: 'account', feature: 'aggrManAccInstName', access: 'r'}])
      && <TextInput readOnly={true}
                    type={'text'}
                    width={'200px'}
                    layout={{grow: 1}}
                    title={'Institution'}
                    dataPoint={{name: 'institutionName', value: account.institutionName}}
                    placeholder={''}/>}
      {!isEntitled([{group: 'account', feature: 'aggrManAccInstName', access: 'r'}])
      && <TextInput readOnly={true}
                    type={'text'}
                    width={'200px'}
                    layout={{grow: 1}}
                    title={'Custody'}
                    dataPoint={{name: 'accountCustody', value: account.custody}}
                    placeholder={''}/>}
      <TextInput readOnly={true}
                 type={'text'}
                 width={'200px'}
                 layout={{grow: 1}}
                 title={'Household'}
                 placeholder={'N/A'}
                 dataPoint={{name: 'name', value: household && household.displayName}}
                 actionIcon={{
                   icon: 'goToIconSmall',
                   action: () => (
                     updateContext && account.householdID && updateContext({
                       containerID: household.containerID || context.view.containerID,
                       name: household.displayName,
                       id: household.id,
                       type: 'HOUSEHOLD',
                       view: ['FIRM', 'BRANCH', 'ADVISOR'].includes(context.type) && context || context.view,
                     })
                   ),
                   description: 'Go to Household',
                 }}
      />
      <TextInput readOnly={true}
                 type={'text'}
                 width={'200px'}
                 layout={{grow: 1}}
                 title={'Client'}
                 dataPoint={{name: 'clientDisplayNameOrdered', value: account.clientDisplayNameOrdered}}
                 placeholder={''}/>
      <TextInput readOnly={true}
                 type={'taxID'}
                 width={'100px'}
                 layout={{grow: 1}}
                 title={'Tax ID'}
                 dataPoint={{name: 'clientTaxID', value: account.clientTaxID}}
                 placeholder={''}/>
    </Row>
    <Row wrap={true}>
      {(!!session.profile.redtail && isEntitled([{group: 'partners', feature: 'redtail', access: 'r'}]))
        ? <Button importance={'secondary'}
                  size={'md'}
                  width={'130px'}
                  label={'View on Redtail'}
                  action={() => history.push(`/engage/redtail?accountID=${accountID}`)}/> : null}
      {(!!session.profile.sfsID && isEntitled([{group: 'partners', feature: 'sfs', access: 'r'}]))
        ? <Button importance={'secondary'}
                  size={'md'}
                  label={'View on Salesforce'}
                  action={() => history.push(`/engage/sfs/sso?accountID=${accountID}`)}/> : null}
      {(!!session.profile.wealthbox && isEntitled([{group: 'partners', feature: 'wealthbox', access: 'r'}]))
        ? <Button importance={'secondary'}
                  size={'md'}
                  width={'130px'}
                  label={'View on Wealthbox'}
                  action={() => history.push(`/engage/wealthbox?accountID=${accountID}`)}/> : null}
      {(account.householdTppAlerts
        && advisorID === session.profile.id
        && !['MANAGED', 'MANUAL'].includes(account.sourceType)
        && isEntitled([{group: 'alerts', feature: 'tpp', access: 'r'}]))
        ? <Button label={'Preferences'}
                  width={'130px'}
                  size={'md'}
                  importance={'secondary'}
                  action={() => openModal({
                    type: 'PrefModal',
                    panelName: 'accountAlerts',
                    cat: {name: 'advisors', id: session.profile.id},
                    target: {name: 'accounts', id: account.id},
                    preferenceButtons: {
                      openHoldingsTPP: (core, cat, target) => openModal({
                        type: 'TppAlertBulkModal',
                        core,
                        cat,
                        target,
                      }),
                    },
                  })}/> : null}
      {isEntitled([{group: 'reports', feature: 'core', access: 'c'}])
      && <Button importance={'secondary'}
                 width={'130px'}
                 size={'md'}
                 label={'Generate Report'}
                 action={() => openModal({type: 'ContextReportModal', context})}/>}
    </Row>
  </Column>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AccountInfoSummary);

AccountInfoSummary.propTypes = {
  accountID: PropTypes.number.isRequired,
  advisorID: PropTypes.number,
  session: PropTypes.object,
  openModal: PropTypes.func.isRequired,
  updateContext: PropTypes.func,
};
